import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Select, Space, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";

import salesRevenueApi from "../../../api/salesRevenueApi";

const STATUS_META = {
    GREEN: { color: "green", text: "数据齐" },
    YELLOW: { color: "gold", text: "缺周次可算" },
    RED: { color: "red", text: "数据不全" },
};

const REASON_TEXT = {
    NO_LEGS: "未挑段",
    MISSING_FUEL_BURN: "缺油耗",
    MISSING_PRICE: "缺市场价",
    NO_BASE_PRICE: "合同缺基准价",
};

function extractError(error, fallback = "操作失败") {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") return detail.detail || fallback;
    return error?.message || fallback;
}

function formatAmount(value) {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return "—";
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * PER_LEG_BURN 批量预览 / 生成面板（应收页「燃油调整计算」模态框的「按段油耗」页签）。
 * 自包含：选月份 + 客户 → 预览 → 勾选可选行 → 批量生成 → 结果汇总。
 */
export default function PerLegBurnBatchPanel({ customerOptions = [], defaultMonth, canGenerate, messageApi, modalApi, onGenerated }) {
    const [month, setMonth] = useState(defaultMonth || dayjs());
    const [customerIds, setCustomerIds] = useState([]);
    const [previewing, setPreviewing] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [preview, setPreview] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const items = useMemo(() => preview?.items || [], [preview]);
    const selectableKeys = useMemo(() => items.filter((item) => item.selectable).map((item) => item.booking_id), [items]);

    const handlePreview = useCallback(async () => {
        if (!month) {
            messageApi.warning("请选择月份");
            return;
        }
        setPreviewing(true);
        setSelectedRowKeys([]);
        try {
            const response = await salesRevenueApi.previewPerLegBurnBatch({
                month: month.format("YYYY-MM"),
                customer_ids: customerIds,
                contract_ids: [],
            });
            setPreview(response.data);
        } catch (error) {
            messageApi.error(extractError(error, "预览失败"));
        } finally {
            setPreviewing(false);
        }
    }, [month, customerIds, messageApi]);

    const runGenerate = useCallback(async () => {
        setGenerating(true);
        try {
            const response = await salesRevenueApi.generatePerLegBurnBatch({
                month: month.format("YYYY-MM"),
                booking_ids: selectedRowKeys,
            });
            const data = response.data?.data || {};
            // 金额按币种分桶返回（不同合同币种可能不同），如 {USD: 3000, EUR: 500}
            const totalsText = Object.entries(data.total_amounts || {})
                .map(([currency, amount]) => `${formatAmount(amount)} ${currency}`)
                .join("、");
            modalApi.success({
                title: "批量生成完成",
                content: (
                    <div>
                        <p>{`已生成 ${data.generated_count || 0} 单${totalsText ? `，合计 ${totalsText}` : ""}。`}</p>
                        <p>{`跳过：数据不齐 ${data.skipped_incomplete_count || 0} 单、已存在 ${data.skipped_existing_count || 0} 单。`}</p>
                    </div>
                ),
            });
            await handlePreview();
            if (onGenerated) await onGenerated();
        } catch (error) {
            messageApi.error(extractError(error, "批量生成失败"));
        } finally {
            setGenerating(false);
        }
    }, [month, selectedRowKeys, handlePreview, messageApi, modalApi, onGenerated]);

    const handleGenerate = () => {
        if (!selectedRowKeys.length) {
            messageApi.warning("请先勾选要生成的订单");
            return;
        }
        modalApi.confirm({
            title: `确认批量生成 ${selectedRowKeys.length} 单燃油应收？`,
            content: "数据不齐的单会被后端自动跳过，已存在燃油应收的单不会重复生成。",
            okText: "批量生成",
            cancelText: "取消",
            onOk: runGenerate,
        });
    };

    const columns = [
        { title: "订单号", dataIndex: "booking_no", width: 150 },
        { title: "客户", dataIndex: "customer_name", render: (value) => value || "—" },
        { title: "合同", dataIndex: "contract_no", render: (value) => value || "—" },
        {
            title: "涉及机场月份",
            dataIndex: "airports_months",
            render: (value) => (value?.length ? value.join("、") : "—"),
        },
        {
            title: "状态",
            dataIndex: "status",
            width: 130,
            render: (value, record) => {
                const meta = STATUS_META[value] || { color: "default", text: value };
                const tag = <Tag color={meta.color}>{meta.text}</Tag>;
                if (value === "RED" && record.reasons?.length) {
                    const reasonText = record.reasons.map((r) => REASON_TEXT[r] || r).join("、");
                    return <Tooltip title={reasonText}>{tag}</Tooltip>;
                }
                return tag;
            },
        },
        {
            title: "预算燃油",
            dataIndex: "fuel_amount_estimate",
            width: 130,
            render: (value, record) => (value == null ? "—" : `${formatAmount(value)} ${record.currency || ""}`),
        },
        {
            title: "已存在应收",
            dataIndex: "has_existing_receivable",
            width: 110,
            render: (value, record) => (value ? <Tag color="blue">{record.existing_receivable_no || "已存在"}</Tag> : "—"),
        },
    ];

    return (
        <div>
            <Space wrap style={{ marginBottom: 12 }}>
                <DatePicker picker="month" value={month} onChange={setMonth} allowClear={false} />
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: 260 }}
                    placeholder="全部客户"
                    value={customerIds}
                    options={customerOptions}
                    onChange={setCustomerIds}
                    maxTagCount="responsive"
                />
                <Button type="primary" loading={previewing} onClick={handlePreview}>
                    预览
                </Button>
            </Space>

            {preview ? (
                <>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 8 }}
                        message={`共 ${items.length} 单：数据齐 ${preview.green_count} / 缺周次可算 ${preview.yellow_count} / 数据不全 ${preview.red_count}；已存在燃油应收 ${preview.existing_count} 单。红行不可选。`}
                    />
                    <Space style={{ marginBottom: 8 }}>
                        <Button size="small" disabled={!selectableKeys.length} onClick={() => setSelectedRowKeys(selectableKeys)}>
                            全选可选
                        </Button>
                        <Button size="small" disabled={!selectedRowKeys.length} onClick={() => setSelectedRowKeys([])}>
                            取消全选
                        </Button>
                        <span style={{ color: "var(--sales-text-secondary)" }}>已选 {selectedRowKeys.length} 单</span>
                    </Space>
                    <Table
                        rowKey="booking_id"
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={items}
                        scroll={{ x: 900, y: 360 }}
                        locale={{ emptyText: "该月暂无按段油耗订单" }}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: setSelectedRowKeys,
                            getCheckboxProps: (record) => ({ disabled: !record.selectable }),
                        }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                        {canGenerate ? (
                            <Button type="primary" loading={generating} disabled={!selectedRowKeys.length} onClick={handleGenerate}>
                                批量生成（{selectedRowKeys.length}）
                            </Button>
                        ) : null}
                    </div>
                </>
            ) : (
                <Alert type="info" showIcon message="选择月份与客户后点「预览」，扫描当月按段油耗订单的数据完整性。" />
            )}
        </div>
    );
}
